/**
 * view.js — frontend only.
 * - Delega la logica animazione a effects.js
 * - IntersectionObserver: pausa quando fuori viewport, riprende quando rientra
 * - oneShot: esegue l'animazione una sola volta
 */

import { runEffect } from './effects.js';

document.querySelectorAll('.typewriter-text').forEach((el) => {
	const settings = {
		words: JSON.parse(el.dataset.words || '[]'),
		effect: el.dataset.effect || 'typewriter',
		animateBy: el.dataset.animateBy || 'letter',
		typeSpeed: parseInt(el.dataset.typeSpeed, 10) || 100,
		deleteSpeed: parseInt(el.dataset.deleteSpeed, 10) || 60,
		pauseDuration: parseInt(el.dataset.pause, 10) || 1500,
		loop: el.dataset.loop === '1',
		oneShot: el.dataset.oneShot === '1',
		easing: el.dataset.easing || 'linear',
		scrambleCycles: parseInt(el.dataset.scrambleCycles, 10) || 8,
		slideDirection: el.dataset.slideDirection || 'up',
	};

	if (!settings.words.length) return;

	// ── Stato DOM per stagger / slide ─────────────────────────────────────────
	// Gli span vengono ricreati solo quando cambia la parola, non ad ogni tick,
	// per consentire alle transizioni CSS di funzionare correttamente.

	let currentWord = null;
	let charSpans = [];
	let morphSpan = null; // usato da morphblur

	function buildCharSpans(chars, extraClass = '') {
		el.innerHTML = '';
		charSpans = chars.map(({ char }) => {
			const span = document.createElement('span');
			span.className = `tw-char tw-char--hidden${extraClass ? ' ' + extraClass : ''}`;
			span.textContent = char === ' ' ? '\u00A0' : char;
			el.appendChild(span);
			return span;
		});
	}

	function onTick(payload) {
		// ── typewriter / scramble ─────────────────────────────────────────────
		if (payload.text !== undefined) {
			if (charSpans.length || morphSpan) {
				charSpans = [];
				morphSpan = null;
				el.innerHTML = '';
			}
			el.textContent = payload.text;
			currentWord = null;
			return;
		}

		// ── stagger / slide ───────────────────────────────────────────────────
		if (payload.chars) {
			const word = payload.chars.map((c) => c.char).join('');
			const dirClass = payload.direction
				? `tw-char--dir-${payload.direction}`
				: '';

			if (word !== currentWord) {
				// Nuova parola: ricrea gli span tutti hidden, poi aggiorna nel frame successivo
				currentWord = word;
				buildCharSpans(payload.chars, dirClass);
				requestAnimationFrame(() => {
					payload.chars.forEach(({ state }, i) => {
						if (charSpans[i])
							charSpans[i].className =
								`tw-char tw-char--${state}${dirClass ? ' ' + dirClass : ''}`;
					});
				});
			} else {
				payload.chars.forEach(({ state }, i) => {
					if (charSpans[i])
						charSpans[i].className =
							`tw-char tw-char--${state}${dirClass ? ' ' + dirClass : ''}`;
				});
			}
			return;
		}

		// ── morphblur ─────────────────────────────────────────────────────────
		if (payload.word !== undefined) {
			// Crea lo span la prima volta o se non esiste
			if (!morphSpan) {
				el.innerHTML = '';
				charSpans = [];
				morphSpan = document.createElement('span');
				morphSpan.className = 'tw-morph';
				el.appendChild(morphSpan);
			}

			if (payload.state === 'hidden') {
				// Aggiorna il testo mentre è invisibile, senza transizione
				morphSpan.classList.add('tw-morph--notransition');
				morphSpan.textContent = payload.word;
				morphSpan.className =
					'tw-morph tw-morph--hidden tw-morph--notransition';
				// Forza reflow per resettare la transizione
				void morphSpan.offsetWidth;
				morphSpan.classList.remove('tw-morph--notransition');
			} else {
				morphSpan.className = `tw-morph tw-morph--${payload.state}`;
			}
			return;
		}
	}

	function onStop() {
		// oneShot: lascia l'ultimo stato visibile, niente da fare
	}

	// ── Avvio con IntersectionObserver ────────────────────────────────────────

	let stopFn = null;
	let hasPlayed = false; // per oneShot

	function startAnimation() {
		if (settings.oneShot && hasPlayed) return;
		hasPlayed = true;

		// Per oneShot, sovrascriviamo loop = false nelle settings passate all'effetto
		const effectSettings = settings.oneShot
			? { ...settings, loop: false }
			: settings;

		stopFn = runEffect(
			effectSettings.effect,
			effectSettings,
			onTick,
			onStop
		);
	}

	function stopAnimation() {
		if (stopFn) {
			stopFn();
			stopFn = null;
		}
	}

	const observer = new IntersectionObserver(
		(entries) => {
			entries.forEach((entry) => {
				if (entry.isIntersecting) {
					// Elemento visibile: avvia o riprendi
					if (!stopFn) startAnimation();
				} else {
					// Elemento fuori viewport: ferma i timer
					// Non resettiamo lo stato — l'animazione riprende da capo al rientro
					// (comportamento più pulito di riprendere a metà)
					stopAnimation();
				}
			});
		},
		{ threshold: 0.1 } // scatta quando almeno il 10% è visibile
	);

	observer.observe(el);
});
